import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Highlight, themes } from "prism-react-renderer";
import Button from "@/components/ui/button";
import { copyToClipboard } from "@/lib/utils";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";
import { useAuth } from "@/modules/auth";

const PLATFORM_GUIDES = [
	{
		id: "framer",
		name: "Framer",
		logo: "/images/embed-doc-logos/logo-framer.png",
		steps: [
			"In Framer, go to Insert → Embed.",
			"With the Embed component selected, set the Type to HTML in the right-hand sidebar.",
			"Paste the widget's HTML script code into the code field.",
			"Drag and position the Embed component on the canvas as needed, then resize it to fit your layout.",
			'Set the height to "Fit" for the embed element so it adapts to the widget content.',
		],
		docsUrl: "https://www.framer.com/help/articles/how-to-add-an-iframe-or-embed-script",
	},
	{
		id: "wordpress",
		name: "WordPress",
		logo: "/images/embed-doc-logos/logo-wordpress.png",
		steps: [
			"In your WordPress dashboard, go to the page or post where you want to embed the widget.",
			"Click the + button to add a new block and search for Custom HTML.",
			"Select the Custom HTML block and paste the widget's HTML embed code into the block.",
			"Preview to ensure the widget displays correctly, then publish or update the page/post.",
		],
		docsUrl: "https://wordpress.com/support/code",
	},
	{
		id: "notion",
		name: "Notion",
		logo: "/images/embed-doc-logos/logo-notion.png",
		steps: [
			"In a Notion page, type /embed where you want the widget to appear and select Embed.",
			"Paste the widget embed URL into the Embed Link field.",
			"Click Embed link, then drag the block's handles to resize the frame so the widget is fully visible.",
		],
		docsUrl: "https://www.notion.com/help/embed-and-connect-other-apps",
	},
	{
		id: "canva",
		name: "Canva",
		logo: "/images/embed-doc-logos/logo-canva.png",
		steps: [
			"In Canva, go to Apps → Embed.",
			"Paste the widget embed URL into the Web Link field.",
			"Click Add to design, then resize and position the embedded frame within your design as needed.",
		],
		docsUrl: "https://www.canva.com/en_gb/help/embed-media",
	},
	{
		id: "wix",
		name: "Wix",
		logo: "/images/embed-doc-logos/logo-wix.png",
		steps: [
			'In the Wix Editor, go to Add → Embed → Embed Code (for example, "Embed HTML").',
			"Add the Embed element to your page, then double-click it to open its settings.",
			"In the popover, ensure Code (or HTML code) is selected in the Choose what to embed field.",
			"Paste the widget's HTML embed code into the Enter HTML embed code field.",
			"Resize and position the frame so that the widget is displayed correctly on the page.",
		],
		docsUrl: "https://support.wix.com/en/article/wix-editor-embedding-a-site-or-a-widget",
	},
	{
		id: "webflow",
		name: "Webflow",
		logo: "/images/embed-doc-logos/logo-webflow.png",
		steps: [],
		docsUrl: "https://help.webflow.com/hc/en-us/articles/33961332238611-Custom-code-embed",
	},
	{
		id: "shopify",
		name: "Shopify",
		logo: "/images/embed-doc-logos/logo-shopify.png",
		steps: [],
		docsUrl: "https://help.shopify.com/en/manual/shopify-admin/productivity-tools/rich-text-editor#add-html-content-with-the-rich-text-editor",
	},
	{
		id: "squarespace",
		name: "Squarespace",
		logo: "/images/embed-doc-logos/logo-squarespace.png",
		steps: [],
		docsUrl: "https://support.squarespace.com/hc/en-us/articles/206543617-Embed-blocks",
	},
] as const;

interface EmbedCodeModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	slug: string;
	supportedDeliveryTypes: string[];
}

function CodeBlock({ code, onCopied }: { code: string; onCopied: () => void }) {
	const { currentWorkspaceId } = useAuth();
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		try {
			await copyToClipboard(code);
			if (currentWorkspaceId) useOnboardingStore.getState().completeStep(currentWorkspaceId, "embed_on_website");
			setCopied(true);
			onCopied();
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	return (
		<div className="relative group">
			<Highlight theme={themes.vsLight} code={code} language="html">
				{({ style, tokens, getLineProps, getTokenProps }) => (
					<pre className="text-sm whitespace-pre font-mono p-5 rounded-lg border border-gray-200 overflow-x-auto" style={{ ...style, margin: 0, backgroundColor: "#f9fafb" }}>
						{tokens.map((line, i) => (
							<div key={i} {...getLineProps({ line })}>
								{line.map((token, key) => (
									<span key={key} {...getTokenProps({ token })} />
								))}
							</div>
						))}
					</pre>
				)}
			</Highlight>
			<button
				onClick={handleCopy}
				className="absolute top-2.5 right-2.5 p-1.5 rounded-md bg-white border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 opacity-0 group-hover:opacity-100 transition-opacity"
			>
				{copied ? (
					<svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
					</svg>
				) : (
					<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
						/>
					</svg>
				)}
			</button>
		</div>
	);
}

function PlatformGuides() {
	const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
	const selected = PLATFORM_GUIDES.find((p) => p.id === selectedPlatform);

	return (
		<div className="mb-6">
			<div className="flex items-center gap-2 mb-3">
				<div className="flex-1 h-px bg-gray-200" />
				<span className="text-xs font-medium text-tertiary uppercase tracking-wide">Where Are You Embedding?</span>
				<div className="flex-1 h-px bg-gray-200" />
			</div>

			{/* Logo grid */}
			<div className="grid grid-cols-4 gap-2 mb-3">
				{PLATFORM_GUIDES.map((platform) => (
					<button
						key={platform.id}
						onClick={() => setSelectedPlatform(selectedPlatform === platform.id ? null : platform.id)}
						className={`flex flex-col items-center gap-1.5 px-2 py-2.5 rounded-lg border text-xs font-medium transition-all ${
							selectedPlatform === platform.id ? "border-gray-900 bg-white text-gray-900" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
						}`}
					>
						<img src={platform.logo} alt={platform.name} className="w-7 h-7 object-contain" />
						{platform.name}
					</button>
				))}
			</div>

			{/* Content panel */}
			{selected && (
				<div className="rounded-lg border border-gray-200 bg-gray-50 p-4 animate-in fade-in duration-150">
					<h4 className="text-sm font-medium text-gray-900 mb-2">Embedding in {selected.name}</h4>
					{selected.steps.length > 0 ? (
						<ol className="list-decimal list-outside ml-4 space-y-1.5 text-sm text-gray-600 mb-3">
							{selected.steps.map((step, i) => (
								<li key={i}>{step}</li>
							))}
						</ol>
					) : (
						<p className="text-sm text-gray-500 mb-3">Refer to the official documentation for embedding instructions.</p>
					)}
					<a href={selected.docsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-700">
						Official documentation
						<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
						</svg>
					</a>
				</div>
			)}
		</div>
	);
}

export default function EmbedCodeModal({ open, onOpenChange, slug, supportedDeliveryTypes }: EmbedCodeModalProps) {
	const hasScript = supportedDeliveryTypes.includes("script");
	const hasIframe = supportedDeliveryTypes.includes("iframe");

	const isLocal = import.meta.env.VITE_CLIENT_WIDGETS_URL?.includes("localhost");
	const scriptCode = `<div data-sau-widget-slug="${slug}" style="width: 100%"></div>\n<script type="module" src="${import.meta.env.VITE_CLIENT_WIDGETS_URL}${isLocal ? "/dist" : ""}/widgets.min.js"></script>`;
	const embedCode = `${import.meta.env.VITE_CLIENT_IFRAMES_URL}/w/${slug}`;

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-2xl p-6 modal-content max-h-[90vh] overflow-y-auto">
					{/* Icon */}
					<div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-blue-100">
						<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
						</svg>
					</div>

					{/* Title */}
					<Dialog.Title className="text-lg font-semibold text-gray-900 text-center mb-2">Embed Widget</Dialog.Title>

					{/* Description */}
					<Dialog.Description className="text-sm text-tertiary text-center mb-6 max-w-xs mx-auto">
						Copy the code below and paste it into your website's HTML to embed this widget.
					</Dialog.Description>

					{/* Embed Script Section */}
					{hasScript && (
						<div className="mb-5">
							<div className="flex items-center gap-2 mb-2">
								<h3 className="text-sm font-medium text-gray-900">Embed Code</h3>
								{hasIframe && <span className="text-xs font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded">Recommended</span>}
							</div>
							<CodeBlock code={scriptCode} onCopied={() => {}} />
						</div>
					)}

					{/* iFrame Section */}
					{hasIframe && (
						<div className="mb-6">
							<div className="mb-2">
								<h3 className="text-sm font-medium text-gray-900">Embed URL</h3>
								<p className="text-xs text-tertiary mt-0.5">For Notion, Framer, Canva and other no-code tools that don't support scripts.</p>
							</div>
							<CodeBlock code={embedCode} onCopied={() => {}} />
						</div>
					)}

					{/* Platform Guides */}
					{hasIframe && <PlatformGuides />}

					{/* Actions */}
					<div className="flex">
						<Button variant="light" className="flex-1" onClick={() => onOpenChange(false)}>
							Close
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
